import { useState, type FormEvent } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
  ArrowLeft, Eye, EyeOff, Lock, Mail, ShieldAlert, AlertTriangle,
  Timer, Ban, ShieldCheck,
} from "lucide-react"
import { useAuth } from "../../context/auth-context"
import { ApiError } from "../../lib/api"
import { SCHOOL, SECURITY } from "../../lib/constants"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Alert } from "../../components/ui/Alert"
import { Card, CardContent } from "../../components/ui/Card"
import { ThemeToggle } from "../../components/ui/ThemeToggle"

export default function AdminLogin() {
  const navigate = useNavigate()
  const { login, logout } = useAuth()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email || !password) {
      setError("Please enter your email and password.")
      return
    }
    setLoading(true)
    try {
      const { role } = await login(email, password)
      if (role !== "ADMIN" && role !== "SUPER_ADMIN") {
        // Not an admin — log them out and deny access
        await logout()
        setError("Access denied. Admin privileges required.")
        toast.error("Access denied. Admin privileges required.")
        return
      }
      toast.success("Welcome back, Administrator.")
      navigate("/admin")
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError("Login failed. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-ink-950 text-ink-100 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 pattern-dots opacity-10" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-brand-800/20 blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] rounded-full bg-gold-700/10 blur-3xl" aria-hidden="true" />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between p-4 md:p-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-300 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to website
        </Link>
        <ThemeToggle />
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="bg-ink-900/90 border-ink-800 backdrop-blur-sm">
            <CardContent className="p-8">
              {/* Header */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-white/95 p-2 mb-3 shadow-lg">
                  <img src="/logo.png" alt={`${SCHOOL.shortName} logo`} className="w-full h-full object-contain" />
                </div>
                <h1 className="font-display text-2xl font-bold text-ink-100 mb-1">Admin Login</h1>
                <p className="text-sm text-ink-400">
                  Secure access to the {SCHOOL.shortName} administrative dashboard.
                </p>
              </div>

              {error && (
                <Alert variant="danger" className="mb-4 flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <Label htmlFor="email" className="text-ink-200">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="admin@ymscoe.edu.ng"
                      className="pl-9 bg-ink-900 border-ink-700 text-ink-100 placeholder:text-ink-500"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      disabled={loading}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-ink-200">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className="pl-9 pr-9 bg-ink-900 border-ink-700 text-ink-100 placeholder:text-ink-500"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={loading}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-200"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-brand-700 hover:bg-brand-800 dark:bg-brand-700 dark:hover:bg-brand-800"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                      Authenticating...
                    </>
                  ) : "Login to Dashboard"}
                </Button>
              </form>

              {/* First-time setup hint */}
              <div className="mt-4 rounded-lg border border-ink-800 bg-ink-950/60 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-gold-500" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-gold-500">
                    First-time Setup
                  </span>
                </div>
                <p className="text-xs text-ink-400">
                  If this is a new deployment and no super admin exists yet, visit{" "}
                  <Link to="/setup" className="text-brand-400 hover:text-brand-300 underline font-mono">
                    /setup
                  </Link>{" "}
                  to create the first admin account.
                </p>
              </div>

              {/* Security notice */}
              <div className="mt-5 rounded-lg border border-ink-800 bg-ink-950/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldAlert className="w-4 h-4 text-gold-500" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-gold-500">
                    Security Notice
                  </span>
                </div>
                <ul className="space-y-2 text-xs text-ink-300">
                  <li className="flex items-start gap-2">
                    <Timer className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-500" />
                    Sessions expire after <strong className="mx-1 text-ink-100">{SECURITY.SESSION_TIMEOUT_MINUTES} minutes</strong> of inactivity.
                  </li>
                  <li className="flex items-start gap-2">
                    <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-500" />
                    Account locks for <strong className="mx-1 text-ink-100">{SECURITY.LOGIN_LOCKOUT_MINUTES} minutes</strong> after <strong className="mx-1 text-ink-100">{SECURITY.MAX_LOGIN_ATTEMPTS} failed attempts</strong>.
                  </li>
                  <li className="flex items-start gap-2">
                    <Ban className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-500" />
                    Repeated failures from the same IP will trigger an automatic IP ban.
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-500" />
                    All login attempts are recorded for audit purposes.
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-xs text-ink-500">
            Authorised personnel only. Unauthorised access is prohibited and prosecuted.
          </p>
        </div>
      </main>
    </div>
  )
}
